import React, { useState, useEffect, useCallback } from 'react';
import {
  CalendarDaysIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  DocumentChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PhotoIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from 'components/ui';
import { toast } from 'sonner';
import {
  formatAmount,
  formatDate,
  formatTime,
  getCurrencySymbol,
  getStatusIcon,
  getStatusColor,
  getStatusLabel,
  getGatewayIcon,
} from 'utils/paymentFormatters.jsx';
import { PaymentMetaInfo } from './PaymentMetaInfo';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';
import clsx from 'clsx';
import { JWT_HOST_API } from 'configs/auth.config';

const getAttachmentUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const cleanPath = path.replace(/^\/+/, '');
  const cleanBase = JWT_HOST_API?.replace(/\/+$/, '');
  return `${cleanBase}/${cleanPath}`;
};

export const PaymentLinkCard = React.memo(({
  transaction,
  isMobile,
  isCancelling,
  isSyncing,
  onCancel,
  onSync,
  onDownloadInvoice,
  onDownloadReceipt,
  isDownloadingInvoice = false,
  isStudent = false,
  isFeePayment = false,
  onEdit,
  onDelete,
  onVerify,
  isReadOnly = false,
}) => {
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const payments = Array.isArray(transaction?.Payments) ? transaction.Payments : [];
  const primaryPayment = payments.length > 0 ? payments[0] : null;
  const transactionId = primaryPayment?.CfPaymentId;
  const bankReferenceId = primaryPayment?.BankReference;
  const paymentMethod = primaryPayment?.PaymentGroup || primaryPayment?.PaymentMethod;
  const shouldShowPaymentMeta =
    transaction.LinkStatus === 'PAID' && (transactionId || bankReferenceId || paymentMethod);

  // Parse attachments from primaryPayment
  const attachments = React.useMemo(() => {
    if (!primaryPayment) return [];
    if (Array.isArray(primaryPayment.Attachments)) return primaryPayment.Attachments;
    if (typeof primaryPayment.Attachments === 'string') {
      try {
        return JSON.parse(primaryPayment.Attachments) || [];
      } catch (e) {
        console.error("Failed to parse attachments JSON string:", e);
      }
    }
    // Fallback if legacy AttachmentPath is present but Attachments is empty
    if (primaryPayment.AttachmentPath) {
      return [{
        AttachmentId: primaryPayment.PaymentId || 'legacy',
        FilePath: primaryPayment.AttachmentPath,
        FileName: primaryPayment.AttachmentPath.substring(primaryPayment.AttachmentPath.lastIndexOf('/') + 1) || 'Attachment'
      }];
    }
    return [];
  }, [primaryPayment]);

  const hasAttachments = attachments.length > 0;
  const isUpiWithAttachment =
    primaryPayment?.PaymentMethod?.toUpperCase() === 'UPI' && hasAttachments;

  const verifyStatus = typeof transaction.VerifyStatus !== 'undefined' ? transaction.VerifyStatus : (typeof transaction.verifyStatus !== 'undefined' ? transaction.verifyStatus : null);
  const isVerified = verifyStatus === 1 || transaction.Verified || transaction.IsVerified || transaction.verified || transaction.isVerified;
  const isRejected = verifyStatus === 2 || transaction.Rejected || transaction.IsRejected || transaction.rejected || transaction.isRejected;
  const isRefunded = verifyStatus === 3 || transaction.Refunded || transaction.IsRefunded || transaction.refunded || transaction.isRefunded;
  const isOfflinePayment = !transaction.LinkId;
  const canEdit = !isReadOnly && isOfflinePayment && !isVerified && !isRejected;

  const handleCopyLink = () => {
    if (transaction.LinkUrl) {
      navigator.clipboard.writeText(transaction.LinkUrl);
      toast.success('Link copied to clipboard');
    }
  };

  const closeAttachment = useCallback(() => setAttachmentPreview(null), []);

  useEffect(() => {
    if (!attachmentPreview) return;
    const onKey = (e) => { if (e.key === 'Escape') closeAttachment(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [attachmentPreview, closeAttachment]);

  return (
    <>
    <div className="rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-800 sm:p-3">
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between lg:gap-4">
        <div className="flex flex-1 gap-2 sm:gap-2.5">
          <div
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-sm font-semibold sm:h-10 sm:w-10 sm:rounded-xl sm:text-base ${transaction.LinkStatus === 'ACTIVE'
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/25'
                : transaction.LinkStatus === 'CANCELLED'
                   ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/25'
                   : 'bg-amber-100 text-amber-600 dark:bg-amber-900/25'
              }`}
          >
            {getCurrencySymbol(transaction.Currency)}
          </div>

          <div className="flex-1 min-w-0 space-y-1.5 sm:space-y-2">
            <div className="flex flex-wrap items-center gap-1 sm:gap-1.5">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 sm:text-base">
                {formatAmount(transaction.Amount, transaction.Currency || 'INR')}
              </span>

              {(() => {
                return (
                  <>
                    {isRefunded ? (
                      <div className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-700/30 dark:text-gray-400">
                        <ArrowPathIcon className="h-3 w-3" />
                        Payment Refunded
                      </div>
                    ) : isVerified ? (
                      <div className="inline-flex items-center gap-1 rounded-full bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-400"
                           title={(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate) ? `Verified on ${formatDate(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate)} ${formatTime(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate)}` : undefined}>
                        <CheckCircleIcon className="h-3 w-3" />
                        Payment Verified{(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate) ? ` (${formatDate(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate)} ${formatTime(transaction.VerifyDate || transaction.verifyDate || primaryPayment?.VerifyDate || primaryPayment?.verifyDate)})` : ''}
                      </div>
                    ) : isRejected ? (
                      <div className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 ring-1 ring-inset ring-rose-600/20 dark:bg-rose-900/30 dark:text-rose-400">
                        <XCircleIcon className="h-3 w-3" />
                        Payment Rejected
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-900/30 dark:text-amber-400">
                        <ClockIcon className="h-3 w-3" />
                        Pending for verification
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="grid gap-1 text-[11px] sm:gap-1 sm:text-xs">
              <div>
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {transaction.FullName}
                </p>
                <p className="break-words text-[10px] text-gray-500 dark:text-gray-400">
                  {transaction.Email}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {/* S - SOURCE BADGE */}
                {(primaryPayment?.PaymentMethod || primaryPayment?.PaymentGroup || transaction.GatewayName) && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:bg-gray-700 dark:text-gray-300">
                    {getGatewayIcon(primaryPayment?.PaymentMethod || primaryPayment?.PaymentGroup || transaction.GatewayName)}
                    Source: {primaryPayment?.PaymentMethod || primaryPayment?.PaymentGroup || transaction.GatewayName}
                  </span>
                )}

                {/* P - PAID TO BADGE */}
                {(transaction.PaidToName || transaction.PaidTo || primaryPayment?.PaidToName || primaryPayment?.PaidTo) && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-300">
                    Paid To: {transaction.PaidToName || primaryPayment?.PaidToName}
                  </span>
                )}

                {/* R - RECEIVED BY BADGE */}
                {(primaryPayment?.ReceivedByName || primaryPayment?.ReceivedBy) && (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-900/30 dark:text-emerald-300">
                    Received By: {primaryPayment?.ReceivedByName || 'Unknown'}
                  </span>
                )}

                {/* UPI SCREENSHOT ICON */}
                {isUpiWithAttachment && attachments.map((att, index) => {
                  const isPdf = att.FileType?.toLowerCase() === 'application/pdf' || att.FilePath?.toLowerCase().endsWith('.pdf');
                  return (
                    <button
                      key={att.AttachmentId || index}
                      type="button"
                      title={isPdf ? `Open PDF attachment ${index + 1}` : `View UPI payment screenshot ${index + 1}`}
                      onClick={() => {
                        const url = getAttachmentUrl(att.FilePath);
                        if (isPdf) {
                          window.open(url, '_blank');
                        } else {
                          setAttachmentPreview(url);
                        }
                      }}
                      className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20 transition-colors hover:bg-violet-100 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-500/30 dark:hover:bg-violet-900/50"
                    >
                      <PhotoIcon className="h-3 w-3" />
                      {attachments.length > 1 ? `${isPdf ? 'PDF' : 'Screenshot'} ${index + 1}` : (isPdf ? 'View PDF' : 'Screenshot')}
                    </button>
                  );
                })}
              </div>
            </div>

            {transaction.Description && !transaction.Description.toLowerCase().includes('offline payment') && (
              <div className="w-fit max-w-[95%] sm:max-w-[70%] break-words rounded-md border border-gray-100 bg-gray-50/50 p-1.5 text-[11px] text-gray-600 dark:border-gray-700 dark:bg-gray-700/30 dark:text-gray-400">
                {transaction.Description}
              </div>
            )}

            {isRejected && (
              <div className="w-fit max-w-[95%] sm:max-w-[70%] break-words rounded-md border border-red-200 bg-red-50/50 p-1.5 text-[11px] text-red-700 dark:border-red-800/50 dark:bg-red-900/20 dark:text-red-400">
                <span className="font-semibold">Reject Reason: </span>
                {transaction.RemarksForRejection || transaction.remarksForRejection || primaryPayment?.RemarksForRejection || primaryPayment?.remarksForRejection || transaction.Remarks || primaryPayment?.Remarks || transaction.remarks || primaryPayment?.remarks || transaction.RejectReason || primaryPayment?.RejectReason || transaction.Notes || primaryPayment?.Notes || "Reason not provided"}
              </div>
            )}

            {shouldShowPaymentMeta && (
              <PaymentMetaInfo
                transactionId={transactionId}
                bankReferenceId={bankReferenceId}
                paymentMethod={paymentMethod}
                isMobile={isMobile}
              />
            )}

            <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400 sm:gap-3">
              <span className="inline-flex items-center gap-1">
                <CalendarDaysIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                {formatDate(transaction.PaymentDate || transaction.CreatedDate)}
              </span>
              {transaction.LinkUrl && (
                <button
                  type="button"
                  className="inline-flex cursor-pointer items-center gap-1 text-[10px] font-medium text-primary-600 transition-colors hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
                  onClick={handleCopyLink}
                >
                  <ClipboardDocumentIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                  Copy link
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-1.5 pt-1 sm:gap-2 sm:pt-1.5 lg:flex-col lg:items-end lg:justify-start lg:pt-0">
          {(transaction.LinkStatus === 'PAID' || transaction.LinkStatus === 'SUCCESS') && isVerified && (
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton
                className="flex items-center justify-between gap-x-2 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 transition-colors hover:border-blue-400 hover:bg-gray-50 dark:border-dark-600 dark:bg-gray-800 dark:text-dark-200 dark:hover:border-blue-500 dark:hover:bg-dark-700 sm:min-w-[12rem]"
              >
                <span className="truncate">Download receipt</span>
                <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500 transition-transform ui-open:rotate-180" aria-hidden="true" />
              </MenuButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute right-0 z-50 mt-1 w-full min-w-[10rem] origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg ring-0 focus:outline-none dark:border-dark-600 dark:bg-gray-800">
                  <div className="py-1">
                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={() => onDownloadReceipt(transaction, 1)}
                          className={clsx(
                            "w-full px-3 py-1.5 flex items-center gap-2 text-left text-[11px] transition-colors",
                            active ? "bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-dark-200"
                          )}
                        >
                          For Internal
                        </button>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={() => onDownloadReceipt(transaction, 2)}
                          className={clsx(
                            "w-full px-3 py-1.5 flex items-center gap-2 text-left text-[11px] transition-colors",
                            active ? "bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-dark-200"
                          )}
                        >
                          For Student
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Transition>
            </Menu>
          )}

          {isFeePayment && transaction.IsEnrollmentFee && (transaction.LinkStatus === 'PAID' || transaction.LinkStatus === 'SUCCESS') && (
            <Button
              variant="outlined"
              size="xs"
              onClick={() => onDownloadInvoice(transaction)}
              className="min-w-[5.5rem] text-[10px] sm:min-w-[6.5rem]"
              disabled={isDownloadingInvoice}
            >
              {isDownloadingInvoice ? (
                <span className="animate-pulse">...</span>
              ) : (
                <>
                  <DocumentChartBarIcon className="h-3.5 w-3.5" />
                  <span className="ml-1">Invoice</span>
                </>
              )}
            </Button>
          )}

          {!isReadOnly && transaction.LinkStatus === 'ACTIVE' && (
            <Button
              variant="outlined"
              size="xs"
              color="error"
              onClick={() => onCancel(transaction)}
              className="min-w-[5rem] text-[10px] sm:min-w-[6rem]"
              disabled={isCancelling}
            >
              {isCancelling ? '...' : 'Cancel'}
            </Button>
          )}
          {/* Verify + Edit + Delete grouped in one row */}
          {!isReadOnly && ((onVerify && !isVerified && !isRejected && !isRefunded && (transaction.LinkStatus === 'PAID' || !transaction.LinkId)) || canEdit) ? (
            <div className="flex items-center gap-1.5">
              {onVerify && !isVerified && !isRejected && !isRefunded && (transaction.LinkStatus === 'PAID' || !transaction.LinkId) && (
                <Button
                  color="primary"
                  variant="filled"
                  onClick={() => onVerify(transaction)}
                  disabled={primaryPayment && !primaryPayment.ReadyForVerification && transaction.ReadyForVerification === false}
                  className="h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg"
                >
                  Verify
                </Button>
              )}
              {canEdit && (
                <>
                  <Button
                    variant="outlined"
                    isIcon
                    onClick={() => onEdit(transaction)}
                    title="Edit payment"
                    className="size-7 rounded-lg border-gray-300 text-gray-500 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-dark-600 dark:text-gray-400 dark:hover:border-blue-400 dark:hover:bg-blue-950/20 transition-colors"
                  >
                    <PencilIcon className="size-3.5" />
                  </Button>
                  <Button
                    variant="outlined"
                    isIcon
                    onClick={() => onDelete(transaction)}
                    title="Delete payment"
                    className="size-7 rounded-lg border-gray-300 text-gray-500 hover:border-red-500 hover:bg-red-50 hover:text-red-600 dark:border-dark-600 dark:text-gray-400 dark:hover:border-red-400 dark:hover:bg-red-950/20 transition-colors"
                  >
                    <TrashIcon className="size-3.5" />
                  </Button>
                </>
              )}
            </div>
          ) : null}
          {!isReadOnly && transaction.GatewayName !== 'Mannual Payment' && transaction.GatewayName !== 'Manual Payment' && transaction.GatewayCode !== 'MAN' && transaction.LinkId && (
            <Button
              variant="outlined"
              size="xs"
              onClick={() => onSync(transaction)}
              className="min-w-[5.5rem] text-[10px] sm:min-w-[6.5rem]"
              disabled={isSyncing}
            >
              <ArrowPathIcon
                className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`}
              />
              <span className="ml-1">
                {isSyncing ? '...' : 'Refresh'}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
      {/* UPI Attachment Lightbox */}
      {attachmentPreview && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={closeAttachment}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={closeAttachment}
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white ring-2 ring-white/40 transition-all hover:bg-black/90 hover:ring-white/70 hover:scale-110"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Header label */}
            <div className="absolute left-0 top-0 z-10 flex items-center gap-1.5 rounded-br-lg bg-black/50 px-3 py-1.5 text-[11px] font-medium text-white">
              <PhotoIcon className="h-3.5 w-3.5" />
              UPI Payment Screenshot
            </div>

            <img
              src={attachmentPreview}
              alt="UPI payment screenshot"
              className="block max-h-[90vh] max-w-[90vw] rounded-xl object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                toast.error('Failed to load payment screenshot');
                closeAttachment();
              }}
            />
          </div>
        </div>
      )}
    </>
  );
});

PaymentLinkCard.displayName = 'PaymentLinkCard';
